import { getRoleIdFromName } from "../organizationRoles";
import v4 from "uuid";
import { DEVELOPMENT, PRODUCTION } from "../application";

// Map organizations from new API format
export const mapOrganizationsFromNewAPI = (response) => {
  if (!response || !response.payload || !response.payload.data) return [];

  const organizations = response.payload.data;

  return organizations.map((org) => ({
    id: org.id,
    uid: org.creatorId || org.id, // Using creatorId as uid fallback
    uuid: org.id,
    title: org.title,
    description: org.description || "",
    created: new Date(org.created),
    image:
      org.documents && org.documents.length > 0
        ? { url: org.documents[0].fileData, alt: org.documents[0].fileName }
        : null,
    documents: org.documents
      ? org.documents.map((doc) => ({
          id: doc.id,
          filename: doc.fileName,
          url: doc.fileData,
        }))
      : [],
    members: org.members || [],
    applications: org.applications || [],
  }));
};

export const mapOrganizations = (organizations) => {
  // Only support new API format
  if (organizations && organizations.payload && organizations.payload.data) {
    return mapOrganizationsFromNewAPI(organizations);
  }
  return [];
};

// Map single organization from new API format
export const mapOrganizationFromNewAPI = (response) => {
  if (!response || !response.payload) {
    return null;
  }

  // Handle both single object (from /get) and array (from /list with filter)
  let org;
  if (Array.isArray(response.payload.data)) {
    // From list endpoint with filter
    if (response.payload.data.length === 0) {
      return null;
    }
    org = response.payload.data[0];
  } else if (response.payload) {
    // From get endpoint - single object directly in payload
    org = response.payload;
  } else {
    return null;
  }

  return {
    id: org.id,
    uid: org.creatorId || org.id,
    uuid: org.id,
    title: org.title,
    description: org.description || "",
    created: new Date(org.created),
    image:
      org.documents && org.documents.length > 0
        ? { url: org.documents[0].fileData, alt: org.documents[0].fileName }
        : null,
    documents: org.documents
      ? org.documents.map((doc) => ({
          id: doc.id,
          filename: doc.fileName,
          url: doc.fileData,
        }))
      : [],
    members: org.members || [],
    applications: org.applications || [],
  };
};

export const mapOrganization = (organization) => {
  // Only support new API format
  if (organization && organization.payload) {
    return mapOrganizationFromNewAPI(organization);
  }
  return null;
};

export const mapOrganizationCONTENT = (organizationContent) => {
  // Handle new API format
  if (organizationContent?.payload?.data) {
    const applications = organizationContent.payload.data;
    return applications.map((app, index) => ({
      nid: index + 1, // Use index as nid for Thumb component to generate default image
      uuid: app.id,
      title: app.appName || app.title || "Untitled", // Use appName from new API
      created: new Date(app.created),
      postLogoutRedirectUri:
        app.postlogoutRedirectUri || app.postLogoutRedirectUri || "",
      oauthRedirectUri: app.oauthRedirectUri || app.clientUriDev || "",
      allowedGrantTypes: app.allowedGrantTypes || [],
      description: {
        value: app.description || "",
        processed: app.description || "",
      },
      descriptionValue: app.description || "", // Add descriptionValue field for ApplicationListItem
      image: app.appImage
        ? {
            url: app.appImage.imageFile,
            alt: app.appImage.altText || app.appName || "Application",
          }
        : app.image || null,
      subscriptions: app.products || app.subscriptions || [],
      subscriptionsIds:
        app.products?.map((sub) => sub.id) ||
        app.subscriptions?.map((sub) => sub.id) ||
        [],
      clientId: {
        development: app.clientIdDev || app.applicationIdDev || "",
        production: app.clientIdProd || app.applicationIdProd || "",
      },
      applicationStatus: app.appStatus || app.status || "DEVELOPMENT",
      clientSecret: app.clientSecret || app.applicationCredentialsDev || "",
    }));
  }

  // No old Drupal format support needed
  return [];
};

export const mapOrganizationDataFromNewAPI = (response) => {
  if (!response || !response.payload || !response.payload.data) return {};

  const organizations = response.payload.data;

  return organizations.reduce((previous, org) => {
    const mappedOrg = {
      id: org.id,
      uid: org.creatorId || org.id,
      uuid: org.id,
      title: org.title,
      description: org.description || "",
      created: new Date(org.created),
      image:
        org.documents && org.documents.length > 0
          ? { url: org.documents[0].fileData, alt: org.documents[0].fileName }
          : null,
      documents: org.documents
        ? org.documents.map((doc) => ({
            id: doc.id,
            filename: doc.fileName,
            url: doc.fileData,
          }))
        : [],
      members: org.members || [],
      applications: org.applications || [],
    };

    return mappedOrg.uuid
      ? {
          ...previous,
          [mappedOrg.uuid]: mappedOrg,
        }
      : previous;
  }, {});
};

export const mapOrganizationData = (unMappedData) => {
  // Only support new API format
  if (unMappedData && unMappedData.payload && unMappedData.payload.data) {
    return mapOrganizationDataFromNewAPI(unMappedData);
  }
  return {};
};

// Organization members are handled by the new API format only
export const mapOrganizationMembers = (unMappedData) => {
  // New API format would be handled here if needed
  return {};
};

// Stub function for backward compatibility - not used with new API
export const mapOrganizationMember = (datum, included) => {
  return null;
};

export const mapOrganizationRoles = (unMappedData) => {
  if (!unMappedData) return;

  // Only support new API format
  if (unMappedData?.payload?.data) {
    const roles = unMappedData.payload.data;
    return roles.reduce((previous, role) => {
      // Convert role name to expected ID format
      const roleId = getRoleIdFromName(role.name || role.title) || role.id;
      const mappedRole = {
        uuid: role.id,
        id: roleId,
        permissions: role.permissions || [],
      };
      return mappedRole.id
        ? {
            ...previous,
            [mappedRole.id]: mappedRole,
          }
        : previous;
    }, {});
  }

  return {};
};

// Organization content is handled by the new API format only
export const mapOrganizationContent = (unMappedData) => {
  return {};
};

export const mapFileUploadErrors = (unMappedData, fileName) => {
  if (!(unMappedData && unMappedData.errors)) {
    const errorId = v4();
    return {
      [errorId]: {
        title: "Could not upload file",
        detail: "Please check that the file extension is allowed.",
        fileName,
        uuid: errorId,
        created: Date.now(),
      },
    };
  }
  return unMappedData.errors.reduce((previous, datum) => {
    const { title, detail } = datum;
    const mappedDatum = {
      title,
      detail,
      fileName,
      uuid: v4(),
      created: Date.now(),
    };
    return {
      ...previous,
      [mappedDatum.uuid]: mappedDatum,
    };
  }, {});
};

export const mapStatusMessage = (message, type) => {
  return {
    uuid: v4(),
    created: Date.now(),
    description: message,
    type: type,
  };
};
